<template>
  <div>
    <nav class="wc-mobile-nav-v2">
      <router-link
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.SHOP }"
        :to="MOBILE_NAV_PATHS.SHOP"
        data-testid="bottom-nav-shop-tab"
      >
        <WCSvgIcon
          name="shop"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.6875, heightRem: 1.5625 }"
        />
        <span class="nav-tab__label">{{ isCartTabVisible ? $t('shop') : $t('home') }}</span>
      </router-link>

      <router-link
        v-if="showSaveCoupon && isCartTabVisible"
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.REORDER }"
        :to="MOBILE_NAV_PATHS.REORDER"
        data-testid="bottom-nav-reorder-tab"
      >
        <WCSvgIcon
          name="buy-it-again"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.4375, heightRem: 1.625 }"
        />
        <span class="nav-tab__label">{{ $t('reorder') }}</span>
      </router-link>
      <router-link
        v-else
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.LIST }"
        :to="MOBILE_NAV_PATHS.LISTS"
        data-testid="bottom-nav-lists-tab"
      >
        <WCSvgIcon
          name="shopping-lists"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.75, heightRem: 1.45831 }"
        />
        <span class="nav-tab__label">{{ $t('lists') }}</span>
      </router-link>

      <router-link
        v-if="isCartTabVisible"
        class="nav-tab nav-tab--cart"
        :class="{ active: active === MOBILE_NAV_TABS.CART }"
        :to="MOBILE_NAV_PATHS.CART"
        data-testid="bottom-nav-cart-tab"
      >
        <div
          class="nav-cart-circle"
          :class="{
            'nav-cart-circle--active': active === MOBILE_NAV_TABS.CART,
            'nav-cart-circle--has-count': cartItemCount > 0,
          }"
        >
          <WCSvgIcon
            name="shopping-cart"
            class="nav-cart-circle__icon"
            :icon-size="{ widthRem: 2.4375, heightRem: 2.1875 }"
          />
          <span class="nav-cart-label" v-if="cartItemCount > 0">
            {{ cartItemCount }} {{ cartItemCount === 1 ? $t('item') : $t('items') }}
          </span>
        </div>
      </router-link>

      <router-link
        v-if="showSaveCoupon"
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.SAVE }"
        :to="MOBILE_NAV_PATHS.SAVE"
        data-testid="bottom-nav-save-tab"
      >
        <WCSvgIcon
          name="save-coupon"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.75, heightRem: 1.25 }"
        />
        <span class="nav-tab__label">{{ $t('save') }}</span>
      </router-link>
      <router-link
        v-else-if="isCartTabVisible"
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.REORDER }"
        :to="MOBILE_NAV_PATHS.REORDER"
        data-testid="bottom-nav-reorder-tab"
      >
        <WCSvgIcon
          name="buy-it-again"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.4375, heightRem: 1.625 }"
        />
        <span class="nav-tab__label">{{ $t('reorder') }}</span>
      </router-link>
      <template v-else>
        <router-link
          v-if="showBrowseTab"
          class="nav-tab"
          :class="{ active: active === MOBILE_NAV_TABS.BROWSE }"
          :to="getBrowseRoute"
          data-testid="bottom-nav-browse-tab"
        >
          <font-awesome-icon icon="border-all" class="nav-tab__icon" />
          <span class="nav-tab__label">{{ $t('browse') }}</span>
        </router-link>
        <router-link
          v-else
          class="nav-tab"
          :class="{ active: active === MOBILE_NAV_TABS.CONTACT_US }"
          :to="MOBILE_NAV_PATHS.CONTACT_US"
          data-testid="bottom-nav-contact-us-tab"
        >
          <font-awesome-icon icon="phone" class="nav-tab__icon" />
          <span class="nav-tab__label">{{ $t('contactUs') }}</span>
        </router-link>
      </template>

      <router-link
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.SEARCH }"
        :to="MOBILE_NAV_PATHS.SEARCH"
        data-testid="bottom-nav-search-tab"
      >
        <WCSvgIcon
          name="search-icon"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.4375, heightRem: 1.4375 }"
        />
        <span class="nav-tab__label">{{ $t('search') }}</span>
      </router-link>
    </nav>
    <div class="wc-mobile-nav-v2-filler" />
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';
import CartItemControls from '@/modules/cart/mixins/CartControls/CartItemControls';
import OrderTypesMixin from '@/modules/orderTypes/mixins/OrderTypesMixin';
import GiftCardMixin from '@/modules/giftcard/mixins/GiftCardMixin';
import {
  MOBILE_NAV_PATHS,
  MOBILE_NAV_TABS,
  MOBILE_NAV_PATH_TO_TAB,
  ORDER_TYPE_HOME_ROUTE_NAME,
} from '@/constants/AppConstants';

export default {
  mixins: [CartItemControls, OrderTypesMixin, GiftCardMixin],

  data() {
    return {
      navResizeObserver: null,
      MOBILE_NAV_PATHS,
      MOBILE_NAV_TABS,
    };
  },

  mounted() {
    this.updateNavHeightCssVar();
    this.observeNavHeight();
  },

  unmounted() {
    this.disconnectNavHeightObserver();
    document.documentElement.style.setProperty('--mobile-nav-height', '0px');
  },

  methods: {
    updateNavHeightCssVar() {
      const nav = this.$el?.querySelector('.wc-mobile-nav-v2');
      const navHeight = Math.ceil(nav?.getBoundingClientRect?.().height || nav?.offsetHeight || 0);
      // nav is offset up from the viewport bottom by env(safe-area-inset-bottom) (home indicator
      // area on notched iOS devices), so its own height alone understates the space it occupies.
      const height = navHeight + this.getSafeAreaInsetBottom();
      document.documentElement.style.setProperty('--mobile-nav-height', `${height}px`);
    },
    // There's no direct JS API for reading a CSS env() value, so apply it as padding on a
    // temporary offscreen probe element and read back the resolved value via getComputedStyle.
    // env(safe-area-inset-bottom) is supported in all current browsers (Safari 11.2+, Chrome 69+,
    // Firefox 65+, Edge 79+); on older/unsupported browsers the env() fallback and the `|| 0`
    // below both resolve to 0, so this degrades safely rather than throwing.
    getSafeAreaInsetBottom() {
      const probe = document.createElement('div');
      probe.style.position = 'fixed';
      probe.style.bottom = '0';
      probe.style.height = '0';
      probe.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
      probe.style.visibility = 'hidden';
      document.body.appendChild(probe);
      const insetBottom = Number.parseFloat(getComputedStyle(probe).paddingBottom) || 0;
      probe.remove();
      return insetBottom;
    },
    observeNavHeight() {
      if (typeof ResizeObserver !== 'function' || !this.$el) return;
      this.disconnectNavHeightObserver();
      const nav = this.$el.querySelector('.wc-mobile-nav-v2');
      if (!nav) return;

      const resizeObserver = new ResizeObserver(() => {
        this.updateNavHeightCssVar();
      });

      if (!resizeObserver || typeof resizeObserver.observe !== 'function') return;

      this.navResizeObserver = markRaw(resizeObserver);
      this.navResizeObserver.observe(nav);
    },
    disconnectNavHeightObserver() {
      if (this.navResizeObserver && typeof this.navResizeObserver.disconnect === 'function') {
        this.navResizeObserver.disconnect();
      }
      this.navResizeObserver = null;
    },
  },

  computed: {
    ...mapGetters({
      carts: 'cart/nonEmptyCarts',
      cartsCount: 'cart/getCartsCount',
    }),

    active() {
      if (this.$route.name === 'NotFound') {
        return null;
      }
      if (this.$route.name === ORDER_TYPE_HOME_ROUTE_NAME) {
        return MOBILE_NAV_TABS.BROWSE;
      }
      return MOBILE_NAV_PATH_TO_TAB[this.$route.path] || MOBILE_NAV_TABS.SHOP;
    },

    showSaveCoupon() {
      return this.$configuration.allowCouponClipping;
    },

    isCartTabVisible() {
      return this.$configuration.cartEnabled || this.isCustomerModeScan;
    },

    showBrowseTab() {
      return this.$configuration.orderTypesEnabled;
    },

    getBrowseRoute() {
      const orderType = this.activeOrderType || this.visibleOrderTypes?.[0];
      if (orderType?.giftCard) {
        return this.giftCardUrl;
      }
      return this.orderTypeHomeForOrderType(orderType);
    },

    cartItemCount() {
      let count;
      if (this.$configuration.orderTypesEnabled && !this.isCustomerModeScan) {
        count = this.carts.reduce((sum, cart) => sum + this.cartLineItemCount(cart), 0);
      } else {
        count = this.cartLineItemCount();
      }
      return count;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-mobile-nav-v2 {
  position: fixed;
  bottom: env(safe-area-inset-bottom, 0);
  left: 0;
  width: 100%;
  height: convert-px-to-rem(80);
  z-index: map-get($zindex, 'bottom-navigation');
  background-color: var(--white);
  box-shadow: 0 -0.2rem 0.2rem rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: stretch;
  overflow: visible;
}

.wc-mobile-nav-v2-filler {
  position: fixed;
  bottom: 0;
  width: 100%;
  height: convert-px-to-rem(80);
  z-index: map-get($zindex, 'bottom-navigation-filler');
  background-color: var(--white);
}

.nav-tab {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  font-size: $font-size-xs;
  text-decoration: none;
  color: var(--dark-lighter-3, get-color('dark', 'lighter-3'));

  &:hover {
    background-color: var(--gray-100, $gray-100);
  }

  &.active {
    color: var(--primary, $primary);

    .nav-tab__label {
      color: var(--primary, $primary);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: convert-px-to-rem(56);
      height: convert-px-to-rem(6);
      background-color: var(--primary, $primary);
      border-radius: convert-px-to-rem(100);
    }
  }
}

.nav-tab:not(.nav-tab--cart) {
  display: grid;
  grid-template-rows: convert-px-to-rem(28) auto;
  justify-items: center;
  align-content: center;
}

.nav-tab__icon {
  font-size: $font-size-xl;
  height: convert-px-to-rem(28);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-tab__label {
  margin-top: $pad-1;
  font-weight: 400;
  font-size: $font-size-base;
  line-height: normal;
  color: var(--dark-lighter-3, get-color('dark', 'lighter-3'));
}

.nav-tab--cart {
  position: relative;
  overflow: visible;
}

.nav-cart-circle--has-count {
  flex-direction: column;
  gap: convert-px-to-rem(2);
}

.nav-cart-label {
  font-size: $font-size-base;
  font-weight: 700;
  line-height: normal;
  color: var(--primary, $primary);
}

.nav-cart-circle {
  position: absolute;
  top: convert-px-to-rem(-20);
  left: 50%;
  transform: translateX(-50%);
  width: convert-px-to-rem(100);
  height: convert-px-to-rem(100);
  border-radius: 50%;
  background-color: var(--white);
  border: convert-px-to-rem(1) solid var(--gray-300, $gray-300);
  box-shadow: 0 convert-px-to-rem(-2) convert-px-to-rem(6) rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-cart-circle__icon {
  color: var(--primary, $primary);
}

.wc-layout--mobile-default:has(.product-gallery) .wc-mobile-nav-v2,
.wc-layout--mobile-default:has(.product-gallery) .wc-mobile-nav-v2-filler {
  display: none;
}
</style>
